import { Banner } from '@tackle-io/platform-ui';
import { isEmpty } from 'lodash-es';
import { OfficeBuilding } from 'mdi-material-ui';
import InfoItem from 'packages/cosell/src/components/InfoItem/InfoItem';
import { CustomerLookupResponse } from 'packages/tackle-api/src/co-sell-msft/customerLookup';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Grid, makeStyles } from 'vendor/material';
import FormSectionCard from '../components/FormSectionCard';
import Subsection from '../components/Subsection';
import { FormSectionConfig } from '../constants';
import MemberInputFieldset from '../MemberInputFieldset/MemberInputFieldset';
import { AddCustomerFormDrawer } from './AddCustomerForm';
import {
  CustomerSearchFormStep,
  MICROSOFT_CUSTOMER_SEARCH_ERROR_MESSAGE,
  TOAST_CONFIG,
} from './constants';
import {
  getCustomerLookupInputBody,
  getSingleCustomerAccountDetails,
} from './helpers';
import {
  FieldEnum,
  selectedCustomerAccountHiddenFields,
  type SelectedCustomerAccountType,
} from './sectionSchema';

// HOOKS
import { useToast } from 'hooks';
import useToggleState from 'hooks/useToggleState';
import { useCustomerLookup } from 'packages/tackle-api/src/co-sell-msft';
import { useMsftReferralMappedFields } from '../../hooks/useMsftReferralMappedFields/useMsftReferralMappedFields';
import { useReferralFormContext } from '../ReferralFormContext';

const useStyle = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  detailsGroup: {
    '& > *': {
      borderBottom: '1px solid #EBECF0', // neutral-20
    },
  },
}));

const NoCustomerSelectedView = ({
  onAddCustomer,
  customerLookupData,
  isCustomerLookupLoading,
  handleReviewCustomerMatches,
}: {
  onAddCustomer: () => void;
  customerLookupData: CustomerLookupResponse;
  isCustomerLookupLoading: boolean;
  handleReviewCustomerMatches: () => void;
}) => {
  const totalCustomerLookupAccounts =
    (customerLookupData?.managedAccounts?.length || 0) +
    (customerLookupData?.unManagedAccounts?.length || 0);

  return (
    <>
      <Banner
        borderPosition="top"
        title="Customer is required to create a referral."
        type="info"
      />
      {totalCustomerLookupAccounts > 1 ? (
        <>
          <Banner
            borderPosition="top"
            title="We found more than one customer match"
            type="warning"
            body="While we always try to select the most relevant customer based on recent co-sell activity and wins, we found customers with similar profiles and activity. You can select one of the matches or add your own."
          />
          <FormSectionCard.AddButton
            onClick={handleReviewCustomerMatches}
            loading={isCustomerLookupLoading}
          >
            Review customer matches
          </FormSectionCard.AddButton>
        </>
      ) : (
        <>
          <FormSectionCard.BlockMessage>
            You haven't added a customer yet.
          </FormSectionCard.BlockMessage>
          <FormSectionCard.AddButton
            onClick={onAddCustomer}
            loading={isCustomerLookupLoading}
          >
            Add customer
          </FormSectionCard.AddButton>
        </>
      )}
    </>
  );
};

const MaccEligibilityBanner = ({ isEligible }: { isEligible: boolean }) => {
  const config = {
    eligible: {
      title: 'This customer is MACC eligible.',
      body: null,
      type: 'info',
    },
    notEligible: {
      title: 'This customer is not MACC eligible.',
      body: 'MACC ineligible customers are not qualified to participate in Microsoft’s Azure Consumption Commitment (MACC) program. They do not have a committed spend with Azure, and may not be a good fit for marketplace transactions.',
      type: 'warning',
    },
  } as const;

  const content = isEligible ? config.eligible : config.notEligible;

  return (
    <Banner
      borderPosition="top"
      body={content.body}
      title={content.title}
      type={content.type}
    />
  );
};

const CustomerContactFormSection = () => {
  const config = {
    title: 'Customer contact',
    helpText:
      'Adding a valid email and phone for the customer contact helps Microsoft sellers evaluate the opportunity better and increases the acceptance rate.',
  };

  const fieldName = FieldEnum['customerProfile.team'];
  return (
    <Subsection title={config.title} helpText={config.helpText}>
      <MemberInputFieldset fieldName={`${fieldName}[0]`} />
    </Subsection>
  );
};

const CustomerSelectedView = ({
  onSearchAgain,
}: {
  onSearchAgain: () => void;
}) => {
  const formContext = useReferralFormContext();
  const values = formContext.getValues().customerProfile;
  const classes = useStyle();

  return (
    <div data-testid="customer-selected-view" className={classes.content}>
      <MaccEligibilityBanner isEligible={values.isMaccEligible === 'true'} />
      <Box className={classes.detailsGroup}>
        <Box>
          <InfoItem
            itemTitle="Company name"
            value={values.name}
            dataId={'msft-customer-profile-name'}
          />
        </Box>
        <Box>
          <InfoItem
            itemTitle="Location"
            value={`${values.address.addressLine1}, ${values.address.city}, ${values.address.country}`}
          />
        </Box>
        <Grid item container>
          <InfoItem
            itemTitle="Microsoft account ID"
            value={values.ids?.[0]?.id}
          />
          <InfoItem
            itemTitle="MACC eligibility"
            value={values.isMaccEligible === 'true' ? 'Yes' : 'No'}
          />
        </Grid>
      </Box>

      <FormSectionCard.AddButton onClick={onSearchAgain}>
        Search again
      </FormSectionCard.AddButton>

      <CustomerContactFormSection />
    </div>
  );
};

export const CustomerProfileFormSection = () => {
  const { toaster } = useToast();
  const [drawerIsOpen, toggleDrawer] = useToggleState(false);
  const formContext = useReferralFormContext();
  const { getValues, setValue } = formContext;
  const [customerFormStep, setCustomerFormStep] =
    useState<CustomerSearchFormStep>(CustomerSearchFormStep.SEARCH);
  const { crmId } = useParams<{ crmId: string }>();
  const mappedFieldsQuery = useMsftReferralMappedFields({
    crmId,
    enabled: !!crmId,
  });
  const hasMappedFieldsError = mappedFieldsQuery.isError;
  const customerLookupInput = mappedFieldsQuery.mappedData?.customerLookupInput;
  const hasMappedCustomerLookupInput =
    !hasMappedFieldsError &&
    !isEmpty(customerLookupInput) &&
    !!customerLookupInput?.companyName &&
    Object.entries(customerLookupInput?.address)?.some(([_, value]) => {
      return !!value;
    });

  const {
    mutate: performCustomerLookup,
    isLoading: isCustomerLookupLoading,
    data: customerLookupData,
  } = useCustomerLookup();

  useEffect(() => {
    if (!hasMappedCustomerLookupInput) return;

    const customerLookupPayload = getCustomerLookupInputBody({
      formValues: customerLookupInput,
    });

    performCustomerLookup(
      {
        body: customerLookupPayload,
      },
      {
        onError: (error) => {
          toaster({
            message: MICROSOFT_CUSTOMER_SEARCH_ERROR_MESSAGE,
            options: {
              ...TOAST_CONFIG,
              type: 'error',
            },
          });
          // toasterCallback();
          reportError(error);
        },
      },
    );
    return;
    // NOTE: wrapping the performCustomerLookup in useCallback doesn't work in this scenario
    // to prevent infinite rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasMappedCustomerLookupInput,
    customerLookupInput,
    performCustomerLookup,
  ]);

  const totalCustomerLookupAccounts =
    (customerLookupData?.managedAccounts?.length || 0) +
    (customerLookupData?.unManagedAccounts?.length || 0);

  useEffect(() => {
    if (totalCustomerLookupAccounts === 1) {
      setValue(
        'customerProfile',
        getSingleCustomerAccountDetails({ customerLookupData }),
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    }
  }, [totalCustomerLookupAccounts, setValue, customerLookupData]);

  const formValues = getValues();
  const hasSelectedCustomer = !!formValues.customerProfile?.ids?.[0]?.id;

  const handleSelectCustomer = useCallback(
    (data: SelectedCustomerAccountType) => {
      setValue('customerProfile', data, {
        shouldValidate: true,
        shouldDirty: true,
      });
      toggleDrawer();
    },
    [setValue, toggleDrawer],
  );

  const handleReviewCustomerMatches = () => {
    setCustomerFormStep(CustomerSearchFormStep.ACCOUNTS_LIST);
    toggleDrawer();
  };

  const onSearchAgain = () => {
    setCustomerFormStep(CustomerSearchFormStep.SEARCH);
    toggleDrawer();
  };

  return (
    <>
      <FormSectionCard
        title={FormSectionConfig.customerProfile.title}
        icon={
          <OfficeBuilding
            data-testid="office-building-icon"
            style={{ fill: '#253858' }}
          />
        }
        data-id={FormSectionConfig.customerProfile.dataId}
        sectionId={FormSectionConfig.customerProfile.id}
      >
        <div style={{ display: 'none' }}>
          {selectedCustomerAccountHiddenFields.map((fieldName) => (
            <input
              key={fieldName}
              type="hidden"
              ref={formContext.register()}
              name={fieldName}
            />
          ))}
        </div>

        {!hasSelectedCustomer ? (
          <NoCustomerSelectedView
            onAddCustomer={toggleDrawer}
            customerLookupData={customerLookupData}
            isCustomerLookupLoading={isCustomerLookupLoading}
            handleReviewCustomerMatches={handleReviewCustomerMatches}
          />
        ) : (
          <CustomerSelectedView onSearchAgain={onSearchAgain} />
        )}
      </FormSectionCard>
      <AddCustomerFormDrawer
        data-testid="add-customer-form-drawer"
        open={drawerIsOpen}
        onClose={toggleDrawer}
        onSelectItem={handleSelectCustomer}
        customerFormStep={customerFormStep}
        setCustomerFormStep={setCustomerFormStep}
        performCustomerLookup={performCustomerLookup}
        customerLookupData={customerLookupData}
        isCustomerLookupLoading={isCustomerLookupLoading}
      />
    </>
  );
};

export default CustomerProfileFormSection;
